import React, { useState } from 'react';
import { adminEndpoints } from '../services/api';
import { Button, TextField, Box, Typography, Alert } from '@mui/material';

interface ImportCardsFromJsonProps {
  packageId: string;
  onSuccess?: () => void;
}

interface CardJson {
  word?: string;
  forbiddenWords?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  category1?: string;
  category2?: string;
  referenceId?: string;
  status?: 'active' | 'inactive';
}

const extractCards = (parsed: unknown): CardJson[] => {
  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { cards?: unknown }).cards)) {
    return (parsed as { cards: CardJson[] }).cards;
  }

  throw new Error('JSON must be an array of cards or an object with a cards array');
};

const toCreatePayload = (card: CardJson) => ({
  word: card.word,
  forbiddenWords: card.forbiddenWords,
  difficulty: card.difficulty,
  category1: card.category1 ?? '',
  category2: card.category2 ?? '',
  status: card.status,
  referenceId: card.referenceId || undefined,
});

const ImportCardsFromJson: React.FC<ImportCardsFromJsonProps> = ({ packageId, onSuccess }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleImport = async () => {
    try {
      setError(null);
      setSuccess(null);
      setIsLoading(true);

      const parsedData = JSON.parse(jsonInput);
      const cards = extractCards(parsedData);

      cards.forEach((card, index) => {
        if (!card.word) {
          throw new Error(`Card at index ${index} is missing required field: word`);
        }
        if (!card.forbiddenWords || !Array.isArray(card.forbiddenWords) || card.forbiddenWords.length !== 5) {
          throw new Error(`Card at index ${index} must have exactly 5 forbidden words`);
        }
      });

      await adminEndpoints.createCard(packageId, {
        cards: cards.map(toCreatePayload),
      });

      setSuccess(`Successfully imported ${cards.length} cards`);
      setJsonInput('');
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import cards');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Import Cards From JSON
      </Typography>
      
      <TextField
        fullWidth
        multiline
        rows={10}
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        placeholder="Paste your JSON array of cards here"
        variant="outlined"
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleImport}
        disabled={isLoading || !jsonInput.trim()}
      >
        {isLoading ? 'Importing...' : 'Import From JSON'}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        Example JSON format:
        <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
          {`[
  {
    "word": "example1",
    "forbiddenWords": ["word1", "word2", "word3", "word4", "word5"],
    "difficulty": "easy",
    "category1": "category1",
    "category2": "category2",
    "referenceId": "c2JhqqHPcul83YGCozGJ"
  },
  {
    "word": "example2",
    "forbiddenWords": ["word6", "word7", "word8", "word9", "word10"],
    "difficulty": "medium",
    "category1": "category1",
    "category2": "category2",
    "referenceId": ""
  }
]`}
        </pre>
      </Typography>
    </Box>
  );
};

export default ImportCardsFromJson; 